import React, { useState, useCallback, useEffect, useRef } from 'react';
import { HeroSection1 } from '../components/landing/HeroSection1';
import { HeroSection2 } from '../components/landing/HeroSection2';
import { ProblemSection } from '../components/landing/ProblemSection';
import HeroSection from '@/components/HeroSection';
import HeroHeadline from '@/components/HeroHeadline';
import PricingSection from '@/components/PricingSection';
import { PluginShowcase } from '../components/landing/PluginShowcase';
import { CTASection } from '../components/landing/CTASection';
import { Footer } from '../components/landing/Footer';
import { TravelingHand } from '../components/landing/TravelingHand';
import DesktopVideoShowcase from '../components/landing/DesktopVideoShowcase';
import { Toaster } from 'sonner';
import { Link, useNavigate } from 'react-router-dom';
import pluckrLogo from '../assets/images/pluckr-logo.png';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { ErrorStateVideo, MobileIconRow, StopBurningCreditsCTA, SuccessStateVideo, PaymentLogoAnimation } from '@/components/mobile';
import ThemeToggle from '@/components/ThemeToggle';
import { useThemeMode } from '@/hooks/useThemeMode';

export default function LandingPage() {
  // Terminal state is now fully driven by scroll progress for bidirectional reversibility
  const [terminalState, setTerminalState] = useState('error');
  const isMobile = useIsMobile();
  const successVideoRef = useRef(null);
  const pluginGardenRef = useRef(null);
  const navigate = useNavigate();
  const { resolvedTheme } = useThemeMode();
  const isDark = resolvedTheme === 'dark';

  // Redirect logged-in users to dashboard (they shouldn't see landing page)
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  // Scroll-progress-based state updater - called from TravelingHand's onUpdate
  // This enables full reversibility: scroll up reverts to previous states
  const handleScrollProgress = useCallback((progress) => {
    // Terminal state thresholds based on scroll progress
    // progress 0-0.75: ERROR state (plugin still traveling)
    // progress 0.75-0.85: TYPING/INSTALLING state (plugin dropping)
    // progress 0.85-1.0: SUCCESS state (plugin inserted)
    if (progress < 0.75) {
      setTerminalState('error');
    } else if (progress < 0.85) {
      setTerminalState('typing');
    } else {
      setTerminalState('success');
    }
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Toast notifications */}
      <Toaster
        position="top-right"
        theme={resolvedTheme}
        toastOptions={{
          style: {
            background: isDark ? '#282929' : '#ffffff',
            color: isDark ? '#ffffff' : '#1a1a1a',
            border: isDark ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid #e0e0e0',
          },
        }}
      />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/60 dark:bg-[#161616]/80 backdrop-blur-xl border-b border-white/20 dark:border-white/10 shadow-[0_4px_30px_rgba(0,0,0,0.1)] dark:shadow-[0_4px_30px_rgba(0,0,0,0.4)]" style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-20 h-14 md:h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-1.5 md:gap-2" data-testid="nav-logo">
            <img src={pluckrLogo} alt="Pluckr" className="h-6 md:h-8 w-auto" />
            <span className="text-lg md:text-xl font-bold text-[#1a1a1a] dark:text-white" style={{ fontFamily: "'Martel Sans', sans-serif" }}>Pluckr</span>
          </a>
          <div className="hidden md:flex items-center gap-8">
            <a href="#aimcpservers" className="text-[#5a5a5a] dark:text-[#e0e0e0] hover:text-[#1a1a1a] dark:hover:text-white transition-colors">AI MCP Servers</a>
            <a href="#see-the-difference" className="text-[#5a5a5a] dark:text-[#e0e0e0] hover:text-[#1a1a1a] dark:hover:text-white transition-colors">How It Works</a>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <ThemeToggle className="shrink-0" />

            {/* Logged-in users are redirected to /dashboard, so always show Get Early Access */}
            <Link
              to="/early-access"
              className="px-4 md:px-6 py-1.5 md:py-2 rounded-full bg-[#9be38f] text-[#1a1a1a] font-semibold text-sm md:text-base hover:bg-[#8ad37f] transition-colors whitespace-nowrap"
              data-testid="nav-cta-btn"
            >
              <span className="hidden sm:inline">Get Early Access</span>
              <span className="sm:hidden">Join</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Wrapper - Contains both hero sections with proper stacking */}
      {/* On mobile: Show simple hero without scroll animation */}
      {/* On desktop: Show full scroll-driven animation */}
      {isMobile ? (
        <>
          {/* Mobile Hero - Simple vertical layout */}
          <HeroSection1 />

          {/* Mobile-only: Text above 1st video */}
          <div
            className="text-center pt-10 pb-3 px-4 bg-[var(--pluckr-bg-soft)]"
          >
            <h3
              className="text-xl font-semibold text-[#1a1a1a] dark:text-white"
              style={{ fontFamily: "'Martel Sans', sans-serif" }}
            >
              Great builder but Missing Pluckr
            </h3>
          </div>

          {/* Mobile-only: Error State Video */}
          <ErrorStateVideo />

          {/* Mobile-only: Icon Row Section */}
          <MobileIconRow />

          {/* Mobile-only: Pluckr's in house MCPs text */}
          <div
            className="text-center py-4 px-4 bg-[var(--pluckr-bg-soft)]"
          >
            <h3
              className="text-xl font-semibold text-[#1a1a1a] dark:text-white"
              style={{ fontFamily: "'Martel Sans', sans-serif" }}
            >
              Pluckr's <span className="text-[#9be38f]">in-house</span> MCPs
            </h3>
          </div>

          {/* Mobile-only: Payment Logo Animation (scroll-driven) */}
          <PaymentLogoAnimation videoRef={successVideoRef} />

          {/* Mobile-only: Text above 2nd video */}
          <div
            className="text-center pt-6 pb-3 px-4 bg-[var(--pluckr-bg-soft)]"
          >
            <h3
              className="text-xl font-semibold text-[#1a1a1a] dark:text-white"
              style={{ fontFamily: "'Martel Sans', sans-serif" }}
            >
              Even a normal builder + our MCP.
            </h3>
          </div>

          {/* Mobile-only: Success State Video */}
          <SuccessStateVideo ref={successVideoRef} />
        </>
      ) : (
        <div
          className="hero-wrapper relative"
          style={{
            height: '200vh',
            zIndex: 1  // Lower z-index than subsequent content
          }}
        >
          {/* Section 1 - Plugin Garden (sticky to viewport) */}
          <div className="sticky top-0 h-screen" style={{ zIndex: 1 }}>
            <HeroSection1 pluginGardenRef={pluginGardenRef} />
          </div>

          {/* Section 2 - Laptop Terminal (sticky, comes into view as you scroll) */}
          <div className="sticky top-0 h-screen overflow-hidden" style={{ zIndex: 2 }}>
            <HeroSection2 terminalState={terminalState} />
          </div>

          {/* Traveling Hand Overlay - Fixed position, scroll-driven with bidirectional state */}
          <TravelingHand onScrollProgress={handleScrollProgress} pluginGardenRef={pluginGardenRef} />
        </div>
      )}

      {/* Remaining sections - higher z-index to layer above hero */}
      <div style={{ position: 'relative', zIndex: 10, backgroundColor: 'var(--pluckr-bg)' }}>
        {isMobile && (
          <div className="mt-6">
            <StopBurningCreditsCTA />
          </div>
        )}
        <div id="problem">
          <ProblemSection />
        </div>

        {/* Desktop-only: Side-by-side failure/success videos — between "AI-Powered builders" and "The missing production layer" */}
        {!isMobile && <DesktopVideoShowcase />}

        <div id="how-it-works">
          <HeroSection testId="how-it-works-section" />
        </div>

        <HeroHeadline />

        <div id="aimcpservers">
          <PluginShowcase />
        </div>

        <div id="pricing">
          <PricingSection />
        </div>

        <div id="cta">
          <CTASection />
        </div>

        <Footer />
      </div>

      {/* Sticky "Built with Pluckr" badge */}
      <div
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: 9999,
          pointerEvents: 'auto',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: 'var(--pluckr-bg-secondary)',
            border: isDark ? '1px solid rgba(255,255,255,0.9)' : '1px solid var(--pluckr-border)',
            borderRadius: '999px',
            padding: '6px 14px 6px 8px',
            boxShadow: isDark
              ? '0 2px 12px rgba(0,0,0,0.55), 0 1px 3px rgba(0,0,0,0.4)'
              : '0 2px 12px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.06)',
            cursor: 'pointer',
            transition: 'box-shadow 0.2s ease, transform 0.2s ease',
            position: 'relative',
            overflow: 'hidden',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = isDark
              ? '0 4px 20px rgba(0,0,0,0.7), 0 2px 6px rgba(0,0,0,0.55)'
              : '0 4px 20px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.08)';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = isDark
              ? '0 2px 12px rgba(0,0,0,0.55), 0 1px 3px rgba(0,0,0,0.4)'
              : '0 2px 12px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.06)';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          {/* Green light sweep */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: '-100%',
              width: '100%',
              height: '100%',
              background: 'linear-gradient(90deg, transparent 0%, rgba(155,227,143,0.4) 40%, rgba(155,227,143,0.6) 50%, rgba(155,227,143,0.4) 60%, transparent 100%)',
              animation: 'badge-sweep 2s ease-in-out infinite',
              pointerEvents: 'none',
            }}
          />
          <img
            src={pluckrLogo}
            alt="Pluckr"
            style={{ width: '18px', height: '18px', borderRadius: '4px', position: 'relative', zIndex: 1 }}
          />
          <span style={{
            fontSize: '12px',
            fontWeight: 700,
            color: 'var(--pluckr-text)',
            whiteSpace: 'nowrap',
            letterSpacing: '-0.01em',
            position: 'relative',
            zIndex: 1,
          }}>
            Built with <span style={{ color: 'var(--pluckr-green)' }}>Pluckr</span>
          </span>
        </div>
      </div>
    </div>
  );
}
